import { 
  Cart, 
  CartItem, 
  CartItemCreateData,
  createEmptyCart,
  calculateCartTotals
} from '../types';
import { cartService } from './cartService';
import { couponService } from './couponService';
import { getProduct } from './firebase';

/**
 * CartOperations - High-level cart operations for React context
 * Handles business logic and coordinates with cartService
 */
export class CartOperations {
  private cart: Cart;
  private vatSettingsCache: { vatPercentage: number; isInclusive: boolean } | null = null;
  private vatSettingsCacheTime: number = 0;
  private readonly VAT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(initialCart?: Cart) {
    this.cart = initialCart || createEmptyCart();
  }

  /**
   * Get current cart
   */
  public getCart(): Cart {
    return { ...this.cart };
  }

  /**
   * Get total number of items in cart
   */
  public getItemCount(): number {
    return this.cart.totalItems;
  }

  /**
   * Get total price of cart
   */
  public getTotalPrice(): number {
    return this.cart.total;
  }

  /**
   * Add item to cart
   */
  public async addItem(itemData: CartItemCreateData): Promise<void> {
    try {
      // Create new cart item
      const newItem = cartService.createCartItem(itemData);

      // Check for duplicates
      const duplicateItem = cartService.findDuplicateItem(this.cart, newItem);

      if (duplicateItem) {
        // Merge with existing item
        this.updateQuantity(duplicateItem.id, duplicateItem.quantity + newItem.quantity);
      } else {
        // Add new item
        this.cart.items.push(newItem);
        await this.updateCartTotals();
        this.saveCart();
      }
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      throw new Error('Failed to add item to cart. Please try again.');
    }
  }

  /**
   * Remove item from cart
   */
  public async removeItem(itemId: string): Promise<void> {
    try {
      const itemIndex = this.cart.items.findIndex(item => item.id === itemId);
      
      if (itemIndex === -1) {
        throw new Error('Item not found in cart');
      }

      this.cart.items.splice(itemIndex, 1);
      await this.updateCartTotals();
      this.saveCart();
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
      throw new Error('Failed to remove item from cart. Please try again.');
    }
  }

  /**
   * Update item quantity
   */
  public async updateQuantity(itemId: string, quantity: number): Promise<void> {
    try {
      if (quantity < 1) {
        this.removeItem(itemId);
        return;
      }

      // Quantity limit removed to support quantity tiers > 10 for desserts

      const item = this.cart.items.find(item => item.id === itemId);
      
      if (!item) {
        throw new Error('Item not found in cart');
      }

      // For desserts with quantity tiers, we need to recalculate price based on the new tier
      // Fetch product to check if it's a dessert and get quantity tiers
      try {
        const product = await getProduct(item.productId);
        const isDessert = product?.collection === 'Desserts' || product?.collectionId === 'desserts';
        
        if (isDessert && product?.sizes) {
          // Find the size that matches the item's size
          const selectedSize = product.sizes.find(size => size.name === item.size);
          
          if (selectedSize?.quantityTiers && selectedSize.quantityTiers.length > 0) {
            // Find the tier that matches the new quantity
            const newTier = selectedSize.quantityTiers.find(tier => tier.quantity === quantity);
            
            if (newTier) {
              // Calculate additional prices (per unit)
              const additionalPrices = item.flavorAdditionalPrice + 
                                     item.fillingAdditionalPrice + 
                                     item.icingAdditionalPrice + 
                                     item.addOnsAdditionalPrice + 
                                     item.matchingCupcakesAdditionalPrice + 
                                     item.imageOnCakeAdditionalPrice +
                                     (item.baseColorAdditionalPrice || 0);
              
              // New total = tier price + (additional prices * quantity)
              const newTotalPrice = newTier.price + (additionalPrices * quantity);
              const newUnitPrice = newTotalPrice / quantity;
              
              // Update item with new quantity and recalculated prices
              item.quantity = quantity;
              item.totalPrice = Math.round(newTotalPrice * 100) / 100;
              item.unitPrice = Math.round(newUnitPrice * 100) / 100;
              item.updatedAt = new Date();
              
              await this.updateCartTotals();
              this.saveCart();
              return;
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch product for quantity tier recalculation, using standard calculation:', error);
        // Fall through to standard calculation
      }

      // Standard calculation for non-desserts or if product fetch failed
      item.quantity = quantity;
      item.totalPrice = item.unitPrice * quantity;
      item.updatedAt = new Date();

      await this.updateCartTotals();
      this.saveCart();
    } catch (error) {
      console.error('Failed to update item quantity:', error);
      throw new Error('Failed to update item quantity. Please try again.');
    }
  }

  /**
   * Clear entire cart
   */
  public async clearCart(): Promise<void> {
    try {
      this.cart = createEmptyCart();
      this.saveCart();
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw new Error('Failed to clear cart. Please try again.');
    }
  }

  /**
   * Load cart from localStorage
   */
  public loadCart(): void {
    try {
      const savedCart = cartService.loadCart();
      if (savedCart) {
        this.cart = savedCart;
      } else {
        this.cart = createEmptyCart();
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      this.cart = createEmptyCart();
    }
  }

  /**
   * Save cart to localStorage
   */
  private saveCart(): void {
    try {
      cartService.saveCart(this.cart);
    } catch (error) {
      console.error('Failed to save cart:', error);
      throw error;
    }
  }

  /**
   * Update cart totals
   */
  private async updateCartTotals(): Promise<void> {
    try {
      // Check if we have cached VAT settings that are still valid
      const now = Date.now();
      let vatSettings = this.vatSettingsCache;
      
      if (!vatSettings || (now - this.vatSettingsCacheTime) > this.VAT_CACHE_DURATION) {
        console.log('🔄 CartOperations.updateCartTotals - Fetching fresh VAT settings...');
        // Get VAT settings from admin system
        const { calculateVatAmount } = await import('./firebase');
        const vatResult = await calculateVatAmount(this.cart.subtotal);
        
        vatSettings = {
          vatPercentage: vatResult.vatPercentage,
          isInclusive: vatResult.isInclusive
        };
        
        console.log('🔄 CartOperations.updateCartTotals - Fresh VAT settings:', vatSettings);
        
        // Cache the settings
        this.vatSettingsCache = vatSettings;
        this.vatSettingsCacheTime = now;
      } else {
        console.log('🔄 CartOperations.updateCartTotals - Using cached VAT settings:', vatSettings);
      }
      
      const totals = calculateCartTotals(
        this.cart.items, 
        this.cart.couponDiscount || 0,
        vatSettings
      );
      
      console.log('🔄 CartOperations.updateCartTotals - Calculated totals:', {
        vat: totals.vat,
        vatPercentage: totals.vatPercentage,
        isInclusive: totals.isInclusive,
        total: totals.total
      });
      
      this.cart.totalItems = totals.totalItems;
      this.cart.subtotal = totals.subtotal;
      this.cart.vat = totals.vat;
      this.cart.vatPercentage = totals.vatPercentage;
      this.cart.isInclusive = totals.isInclusive;
      this.cart.shipping = totals.shipping;
      this.cart.couponDiscount = totals.couponDiscount;
      this.cart.total = totals.total;
      this.cart.lastUpdated = new Date();
    } catch (error) {
      console.error('Error updating cart totals with VAT settings:', error);
      // Fallback to basic calculation with zero VAT (VAT disabled)
      const totals = calculateCartTotals(
        this.cart.items, 
        this.cart.couponDiscount || 0,
        { vatPercentage: 0, isInclusive: false } // Force VAT disabled
      );
      
      this.cart.totalItems = totals.totalItems;
      this.cart.subtotal = totals.subtotal;
      this.cart.vat = totals.vat;
      this.cart.vatPercentage = totals.vatPercentage;
      this.cart.isInclusive = totals.isInclusive;
      this.cart.shipping = totals.shipping;
      this.cart.couponDiscount = totals.couponDiscount;
      this.cart.total = totals.total;
      this.cart.lastUpdated = new Date();
    }
  }

  /**
   * Check if cart is empty
   */
  public isEmpty(): boolean {
    return this.cart.items.length === 0;
  }

  /**
   * Clear VAT settings cache (call when admin changes VAT settings)
   */
  public clearVatCache(): void {
    this.vatSettingsCache = null;
    this.vatSettingsCacheTime = 0;
  }

  /**
   * Get cart item by ID
   */
  public getItem(itemId: string): CartItem | null {
    return this.cart.items.find(item => item.id === itemId) || null;
  }

  /**
   * Check if item exists in cart
   */
  public hasItem(itemId: string): boolean {
    return this.cart.items.some(item => item.id === itemId);
  }

  /**
   * Get items by product ID
   */
  public getItemsByProduct(productId: string): CartItem[] {
    return this.cart.items.filter(item => item.productId === productId);
  }

  /**
   * Get cart summary for display
   */
  public getCartSummary(): {
    itemCount: number;
    totalItems: number;
    subtotal: number;
    vat: number;
    vatPercentage: number;
    isInclusive: boolean;
    shipping: number;
    couponDiscount: number;
    total: number;
    isEmpty: boolean;
  } {
    return {
      itemCount: this.cart.items.length,
      totalItems: this.cart.totalItems,
      subtotal: this.cart.subtotal,
      vat: this.cart.vat,
      vatPercentage: this.cart.vatPercentage,
      isInclusive: this.cart.isInclusive,
      shipping: this.cart.shipping,
      couponDiscount: this.cart.couponDiscount || 0,
      total: this.cart.total,
      isEmpty: this.isEmpty()
    };
  }

  /**
   * Validate current cart
   */
  public validateCart(): boolean {
    return cartService.validateCart(this.cart);
  }

  /**
   * Get cart statistics
   */
  public getCartStats() {
    return cartService.getCartStats(this.cart);
  }

  /**
   * Export cart data (for debugging or backup)
   */
  public exportCart(): string {
    return JSON.stringify(this.cart, null, 2);
  }

  /**
   * Import cart data (for debugging or restore)
   */
  public importCart(cartData: string): void {
    try {
      const importedCart = JSON.parse(cartData);
      
      // Validate imported cart
      if (cartService.validateCart(importedCart)) {
        this.cart = importedCart;
        this.saveCart();
      } else {
        throw new Error('Invalid cart data');
      }
    } catch (error) {
      console.error('Failed to import cart:', error);
      throw new Error('Failed to import cart data. Please check the format.');
    }
  }

  /**
   * Set cart directly (used for updating with VAT calculations)
   */
  public setCart(cart: Cart): void {
    this.cart = cart;
    this.saveCart();
  }

  /**
   * Update cart with VAT calculations
   */
  public async updateCartWithVat(): Promise<void> {
    try {
      // Use our own VAT calculation logic instead of cartService
      // This ensures consistency with our VAT settings and fallback logic
      await this.updateCartTotals();
      this.saveCart();
    } catch (error) {
      console.error('Failed to update cart with VAT:', error);
      throw new Error('Failed to update cart with VAT calculations.');
    }
  }

  /**
   * Apply coupon to cart
   */
  public async applyCoupon(couponCode: string): Promise<boolean> {
    try {
      // Validate coupon
      const validation = await couponService.validateCoupon(couponCode, this.cart.subtotal);
      
      if (!validation.isValid || !validation.coupon) {
        throw new Error(validation.error || 'Invalid coupon code');
      }

      // Check if coupon system is enabled
      const settings = await couponService.getCouponSettings();
      if (!settings?.isEnabled) {
        throw new Error('Coupon system is currently disabled');
      }

      // Check if multiple coupons are allowed
      if (!settings.allowMultipleCoupons && this.cart.coupon) {
        throw new Error('Only one coupon can be applied per order');
      }

      // Apply coupon
      this.cart.coupon = validation.coupon;
      this.cart.couponDiscount = validation.discountAmount || 0;
      
      // Clear VAT cache to ensure fresh VAT settings are used
      this.clearVatCache();
      await this.updateCartTotals();
      this.saveCart();
      
      return true;
    } catch (error) {
      console.error('Failed to apply coupon:', error);
      throw error;
    }
  }

  /**
   * Remove coupon from cart
   */
  public async removeCoupon(): Promise<void> {
    try {
      this.cart.coupon = undefined;
      this.cart.couponDiscount = 0;
      
      // Clear VAT cache to ensure fresh VAT settings are used
      this.clearVatCache();
      await this.updateCartTotals();
      this.saveCart();
    } catch (error) {
      console.error('Failed to remove coupon:', error);
      throw new Error('Failed to remove coupon. Please try again.');
    }
  }
}
